import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { Link } from 'react-router-dom';
import {
  buildTryBookingFallbackUrl,
  ensureTryBookingWidget,
} from '@/lib/trybookingWidget';

export default function TryBookingWidget({
  eventId,
  widgetType = 'fullEmbed',
  region = 'au',
  sessionLabel,
  backHref = '/events',
  backLabel = 'Back to events',
  className = '',
}) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventId || !containerRef.current) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    ensureTryBookingWidget(containerRef.current, eventId, widgetType, region)
      .then((iframe) => {
        if (cancelled) return;
        if (!iframe) {
          setError('Unable to load the booking widget.');
          return;
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Unable to load the booking widget. Please try again.');
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId, widgetType, region]);

  const fallbackUrl = buildTryBookingFallbackUrl(eventId, region);

  return (
    <div className={`flex flex-col bg-white ${className}`.trim()}>
      <div className="flex items-center gap-3 px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 border-b border-border/60 bg-white shrink-0">
        <Link
          to={backHref}
          className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
        >
          <Icon name="ArrowLeftIcon" size={14} />
          {backLabel}
        </Link>
        <div className="min-w-0 flex-1 text-right sm:text-left">
          <p className="text-xs font-bold uppercase tracking-wide text-primary">Secure ticket booking</p>
          {sessionLabel ? (
            <p className="text-sm font-semibold text-foreground truncate">{sessionLabel}</p>
          ) : null}
        </div>
      </div>

      <div className="relative w-full" style={{ minHeight: 'min(860px, calc(100vh - 12rem))' }}>
        {loading ? (
          <div className="absolute inset-0 z-10 flex min-h-[420px] flex-col items-center justify-center gap-3 bg-white px-6 text-center">
            <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            <p className="text-sm font-semibold text-foreground">Loading booking calendar</p>
          </div>
        ) : null}

        {error ? (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-white px-6 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs font-semibold text-foreground hover:bg-muted"
            >
              Open TryBooking
              <Icon name="ArrowTopRightOnSquareIcon" size={14} />
            </a>
          </div>
        ) : null}

        <div
          ref={containerRef}
          className="tryb-widget w-full overflow-visible"
          style={{
            minHeight: 'min(860px, calc(100vh - 12rem))',
            pointerEvents: loading || error ? 'none' : 'auto',
          }}
          data-type={widgetType}
          data-eid={String(eventId)}
        />
      </div>
    </div>
  );
}
